import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, User, Briefcase, Bell, CheckCircle, ArrowRight, Star } from 'lucide-react';
import Layout from '../../components/Layout';

const VAGAS_SIMULADAS = [
  {
    id: 1,
    titulo: 'Analista de TI',
    empresa: 'Construtora Acre Sul',
    cidade: 'Rio Branco — AC',
    area: 'Tecnologia',
    match: 85,
  },
  {
    id: 2,
    titulo: 'Técnico em Redes',
    empresa: 'Grupo Amazônia Digital',
    cidade: 'Rio Branco — AC',
    area: 'Tecnologia',
    match: 72,
  },
  {
    id: 3,
    titulo: 'Suporte de TI',
    empresa: 'Prefeitura Municipal',
    cidade: 'Cruzeiro do Sul — AC',
    area: 'Tecnologia',
    match: 68,
  },
];

const JORNADA_STEPS = [
  {
    numero: 1,
    titulo: 'Cadastro',
    descricao: 'Cria conta com e-mail e senha',
    icon: '📝',
  },
  {
    numero: 2,
    titulo: 'Perfil com IA',
    descricao: 'Preenche perfil guiado por inteligência artificial',
    icon: '🤖',
  },
  {
    numero: 3,
    titulo: 'Match',
    descricao: 'Recebe sugestões de vagas compatíveis',
    icon: '🎯',
  },
  {
    numero: 4,
    titulo: 'Contratação',
    descricao: 'Empresa entra em contato e fecha contrato',
    icon: '🤝',
  },
];

function MatchBadge({ valor }) {
  const cor =
    valor >= 80
      ? 'bg-green-100 text-green-700 border-green-300'
      : valor >= 70
      ? 'bg-acre-gold/20 text-yellow-700 border-yellow-300'
      : 'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-bold ${cor}`}>
      <Star size={11} />
      {valor}% match
    </span>
  );
}

export default function PreviewCandidato() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Preview — Candidato"
      subtitle="Veja a plataforma como um candidato vê"
    >
      {/* Header explicativo */}
      <div className="bg-acre-dark rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-acre-green/20 flex items-center justify-center">
            <Eye size={20} className="text-acre-green" />
          </div>
          <div>
            <h2 className="font-sora font-bold text-white text-base">
              Visualização — Perspectiva do Candidato
            </h2>
            <p className="text-gray-400 text-xs mt-0.5">
              Esta página simula o que um candidato vê ao usar a plataforma
            </p>
          </div>
        </div>
        <span className="self-start sm:self-auto inline-flex items-center gap-1.5 bg-acre-gold/20 border border-acre-gold/40 text-acre-gold text-xs font-bold px-3 py-1.5 rounded-full">
          <Eye size={12} />
          Modo Admin — Visualização
        </span>
      </div>

      {/* Dashboard simulado do candidato */}
      <h3 className="section-title mb-4">Como o candidato vê o Dashboard</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {/* Card perfil incompleto */}
        <div className="card p-5 border-l-4 border-yellow-400">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-yellow-500" />
            <p className="font-semibold text-acre-dark text-sm">Meu Perfil</p>
          </div>
          <p className="text-xs text-gray-400 mb-2">Complete seu perfil para mais matches</p>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Progresso</span>
            <span className="text-xs font-bold text-yellow-600">65%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-yellow-400 h-2 rounded-full transition-all"
              style={{ width: '65%' }}
            />
          </div>
          <p className="text-xs text-yellow-600 mt-2 font-medium">
            ⚠ Adicione experiências para completar
          </p>
        </div>

        {/* Card matches disponíveis */}
        <div className="card p-5 border-l-4 border-acre-green">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase size={16} className="text-acre-green" />
            <p className="font-semibold text-acre-dark text-sm">Vagas Compatíveis</p>
          </div>
          <p className="font-sora font-black text-3xl text-acre-dark">3</p>
          <p className="text-xs text-gray-400 mt-1">vagas com match para você</p>
          <button className="mt-3 text-xs text-acre-green font-bold underline hover:no-underline">
            Ver todas →
          </button>
        </div>

        {/* Card notificações */}
        <div className="card p-5 border-l-4 border-acre-teal">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-acre-teal" />
            <p className="font-semibold text-acre-dark text-sm">Notificações</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-acre-green mt-1.5 flex-shrink-0" />
              <p className="text-xs text-gray-600">Nova vaga compatível encontrada</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-acre-gold mt-1.5 flex-shrink-0" />
              <p className="text-xs text-gray-600">Empresa visualizou seu perfil</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
              <p className="text-xs text-gray-400">Complete seu perfil (+35%)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de vagas simuladas */}
      <h3 className="section-title mb-4">Vagas Compatíveis (simulado)</h3>

      <div className="space-y-3 mb-8">
        {VAGAS_SIMULADAS.map((vaga) => (
          <div
            key={vaga.id}
            className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-acre-light flex items-center justify-center flex-shrink-0">
                <Briefcase size={18} className="text-acre-green" />
              </div>
              <div>
                <p className="font-semibold text-acre-dark text-sm">{vaga.titulo}</p>
                <p className="text-xs text-gray-500">{vaga.empresa}</p>
                <p className="text-xs text-gray-400">{vaga.cidade} · {vaga.area}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MatchBadge valor={vaga.match} />
              <button className="text-xs bg-acre-green text-white px-3 py-1.5 rounded-lg font-bold hover:bg-acre-mid transition-colors">
                Ver vaga
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Jornada do Candidato */}
      <h3 className="section-title mb-4">Jornada do Candidato</h3>

      <div className="card p-6 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {JORNADA_STEPS.map((step, idx) => (
            <div key={step.numero} className="flex flex-col items-center text-center">
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-acre-light flex items-center justify-center text-2xl mb-3">
                  {step.icon}
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-acre-green flex items-center justify-center">
                  <span className="text-white text-xs font-black">{step.numero}</span>
                </div>
              </div>
              <p className="font-sora font-bold text-acre-dark text-sm mb-1">{step.titulo}</p>
              <p className="text-xs text-gray-400 leading-snug">{step.descricao}</p>
              {idx < JORNADA_STEPS.length - 1 && (
                <div className="hidden sm:flex absolute" />
              )}
            </div>
          ))}
        </div>
        {/* Setas entre steps (visual) */}
        <div className="hidden sm:flex items-center justify-center gap-2 mt-4">
          {JORNADA_STEPS.map((step, idx) => (
            <React.Fragment key={step.numero}>
              <span className="text-xs font-bold text-acre-green bg-acre-light px-2 py-0.5 rounded-full">
                {step.titulo}
              </span>
              {idx < JORNADA_STEPS.length - 1 && (
                <ArrowRight size={14} className="text-gray-300" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Botão */}
      <div className="flex justify-end">
        <button
          onClick={() => navigate('/admin/candidatos')}
          className="inline-flex items-center gap-2 bg-acre-dark text-white font-bold px-5 py-3 rounded-xl hover:bg-acre-green transition-colors"
        >
          <User size={16} />
          Ver candidato real
        </button>
      </div>
    </Layout>
  );
}
